import { EnvVariables } from "@/env-variables";
import * as LocalStorage from "@/storages/LocalStorage";
import { axiosInstance } from "../api/axios-instance";

export const loginApi = async (email: string, password: string) => {
  return axiosInstance.post("/login", { email, password });
};

const COGNITO_IDP_ENDPOINT = "https://cognito-idp.ap-southeast-1.amazonaws.com/";
const COGNITO_CONTENT_TYPE = "application/x-amz-json-1.1";

interface TokenRefreshResponse {
  AuthenticationResult: {
    AccessToken: string;
    IdToken: string;
    ExpiresIn: number;
  };
}

/**
 * Refresh the access token using the refresh token
 * @returns Promise with new tokens
 */
export const refreshToken = async (): Promise<string | null> => {
  try {
    const refreshToken = LocalStorage.getRefreshToken();

    if (!refreshToken) {
      console.error("No refresh token available");
      return null;
    }

    const response = await fetch(COGNITO_IDP_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": COGNITO_CONTENT_TYPE,
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: EnvVariables.clientId,
        UserPoolId: EnvVariables.userPoolId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data: TokenRefreshResponse = await response.json();
    if (data?.AuthenticationResult?.AccessToken) {
      // Store the new access token
      LocalStorage.setAccessToken(data.AuthenticationResult.AccessToken);

      // Return the new access token
      return data.AuthenticationResult.AccessToken;
    }

    return null;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
};

/**
 * Revoke the current refresh token at Cognito so it cannot mint new access tokens after logout.
 * Best effort: failures (e.g. token revocation disabled on the app client) are logged and never
 * block logout. Callers that navigate away afterwards await it with a short bound first (see
 * `signOutRedirect`); `keepalive` is only the fallback that lets a slower request finish while
 * the page navigates to the Cognito logout URL.
 */
export const revokeRefreshToken = async (): Promise<void> => {
  const token = LocalStorage.getRefreshToken();
  if (!token) return;

  try {
    const response = await fetch(COGNITO_IDP_ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": COGNITO_CONTENT_TYPE,
        "X-Amz-Target": "AWSCognitoIdentityProviderService.RevokeToken",
      },
      body: JSON.stringify({
        Token: token,
        ClientId: EnvVariables.clientId,
      }),
    });

    if (!response.ok) {
      console.warn(`Refresh token revocation failed: ${response.status}`);
    }
  } catch (error) {
    console.warn("Error revoking refresh token:", error);
  }
};

/**
 * Check if token is expired based on JWT expiration
 * @param token JWT token
 * @returns boolean indicating if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    // Get the payload part of the JWT
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );

    const { exp } = JSON.parse(jsonPayload);

    // Add a buffer of 60 seconds to refresh token before it actually expires
    const currentTime = Math.floor(Date.now() / 1000) + 60;

    return exp < currentTime;
  } catch (e) {
    console.error("Error checking token expiration:", e);
    return true; // If we can't verify, assume it's expired
  }
};
